import { GetCustomersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/get-customers-paginated.model';
import { GatewayCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/outputs/gateway-customer.model';
import { GatewayUpdateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/update-customer.model';
import { GetNetworksPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/networks/models/inputs/get-networks-paginated.model';
import { GetUsersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/get-users-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { CreateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/customers/create-customer.model';
import { UpdateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/customers/update-customer.model';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  CUSTOMER_FILTERS_LSK,
  CUSTOMER_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import {
  NETWORK_LIST_SORT_FIELD,
  NETWORK_LIST_SORT_ORDER,
} from '@app/feature/network/network-list/network-list.config';
import {
  USER_LIST_SORT_FIELD,
  USER_LIST_SORT_ORDER,
} from '@app/feature/user/user-list/user-list.config';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  CUSTOMER_LIST_DEFAULT_OPTIONS,
  CUSTOMER_LIST_FILTER_NAME_LIST,
  CUSTOMER_LIST_SORT_FIELD,
  CUSTOMER_LIST_SORT_ORDER,
  CustomerListOptionsModel,
} from './customer-list/customer-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const CustomerContext = createContext<GatewayCustomerModel | null>(null);

export const useCustomerContext = (): GatewayCustomerModel => {
  const context = useContext(CustomerContext);

  if (!context) {
    throw new Error(
      'useCustomerContext must be used within CustomerContext.Provider',
    );
  }

  return context;
};

interface CustomerContextProviderProps {
  data: GatewayCustomerModel;
  children: React.ReactNode;
}

export const CustomerContextProvider = ({
  data,
  children,
}: CustomerContextProviderProps): React.JSX.Element => {
  return (
    <CustomerContext.Provider value={data}>{children}</CustomerContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface CustomerListContextProps {
  getPaginatedQueryParameters: GetCustomersPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetCustomersPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
  columnOptions: CustomerListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<CustomerListOptionsModel>>;
}

export const CustomerListContext =
  createContext<CustomerListContextProps | null>(null);

export const useCustomerListContext = (): CustomerListContextProps => {
  const context = useContext(CustomerListContext);

  if (!context) {
    throw new Error(
      'useCustomerListContext must be used within CustomerListContext.Provider',
    );
  }

  return context;
};

interface CustomerListContextProviderProps {
  children: React.ReactNode;
}

export const CustomerListContextProvider = ({
  children,
}: CustomerListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetCustomersPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        isExemptFromBilling: undefined,
        textToSearch: '',
        // Sortable fields
        sortField: CUSTOMER_LIST_SORT_FIELD,
        sortOrder: CUSTOMER_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...CUSTOMER_LIST_FILTER_NAME_LIST,
      ],
      CUSTOMER_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<CustomerListOptionsModel>(
      CUSTOMER_LIST_DEFAULT_OPTIONS,
      CUSTOMER_LIST_OPTIONS_LSK,
    );

  const data = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
      columnOptions,
      setColumnOptions,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  ]);

  return (
    <CustomerListContext.Provider value={data}>
      {children}
    </CustomerListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterCustomerListContext =
  createContext<DeepPartialSkipArrayKey<GetCustomersPaginatedModel> | null>(
    null,
  );

export const useFilterCustomerListContext =
  (): DeepPartialSkipArrayKey<GetCustomersPaginatedModel> => {
    const context = useContext(FilterCustomerListContext);

    if (!context) {
      throw new Error(
        'useFilterCustomerListContext must be used within FilterCustomerListContext.Provider',
      );
    }

    return context;
  };

interface FilterCustomerListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetCustomersPaginatedModel>;
  children: React.ReactNode;
}

export const FilterCustomerListContextProvider = ({
  data,
  children,
}: FilterCustomerListContextProviderProps): React.JSX.Element => {
  return (
    <FilterCustomerListContext.Provider value={data}>
      {children}
    </FilterCustomerListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchCustomerListContext =
  createContext<DeepPartialSkipArrayKey<GetCustomersPaginatedModel> | null>(
    null,
  );

export const useFilterSearchCustomerListContext =
  (): DeepPartialSkipArrayKey<GetCustomersPaginatedModel> => {
    const context = useContext(FilterSearchCustomerListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchCustomerListContext must be used within FilterSearchCustomerListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchCustomerListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetCustomersPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchCustomerListContextProvider = ({
  data,
  children,
}: FilterSearchCustomerListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchCustomerListContext.Provider value={data}>
      {children}
    </FilterSearchCustomerListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const CreateCustomerContext =
  createContext<DeepPartialSkipArrayKey<CreateCustomerModel> | null>(null);

export const useCreateCustomerContext =
  (): DeepPartialSkipArrayKey<CreateCustomerModel> => {
    const context = useContext(CreateCustomerContext);

    if (!context) {
      throw new Error(
        'useCreateCustomerContext must be used within CreateCustomerContext.Provider',
      );
    }

    return context;
  };

interface CreateCustomerContextProviderProps {
  data: DeepPartialSkipArrayKey<CreateCustomerModel>;
  children: React.ReactNode;
}

export const CreateCustomerContextProvider = ({
  data,
  children,
}: CreateCustomerContextProviderProps): React.JSX.Element => {
  return (
    <CreateCustomerContext.Provider value={data}>
      {children}
    </CreateCustomerContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateCustomerContext = createContext<
  | (Pick<UpdateCustomerModel, 'customerId'> & {
      customer: GatewayUpdateCustomerModel;
    })
  | null
>(null);

export const useUpdateCustomerContext = (): Pick<
  UpdateCustomerModel,
  'customerId'
> & {
  customer: GatewayUpdateCustomerModel;
} => {
  const context = useContext(UpdateCustomerContext);

  if (!context) {
    throw new Error(
      'useUpdateCustomerContext must be used within UpdateCustomerContext.Provider',
    );
  }

  return context;
};

interface UpdateCustomerContextProviderProps {
  data: Pick<UpdateCustomerModel, 'customerId'> & {
    customer: GatewayUpdateCustomerModel;
  };
  children: React.ReactNode;
}

export const UpdateCustomerContextProvider = ({
  data,
  children,
}: UpdateCustomerContextProviderProps): React.JSX.Element => {
  return (
    <UpdateCustomerContext.Provider value={data}>
      {children}
    </UpdateCustomerContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface CustomerNetworkListContextProps {
  getPaginatedQueryParameters: GetNetworksPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetNetworksPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const CustomerNetworkListContext =
  createContext<CustomerNetworkListContextProps | null>(null);

export const useCustomerNetworkListContext =
  (): CustomerNetworkListContextProps => {
    const context = useContext(CustomerNetworkListContext);

    if (!context) {
      throw new Error(
        'useCustomerNetworkListContext must be used within CustomerNetworkListContext.Provider',
      );
    }

    return context;
  };

interface CustomerNetworkListContextProviderProps {
  data: { customer: GatewayCustomerModel };
  children: React.ReactNode;
}

export const CustomerNetworkListContextProvider = ({
  data,
  children,
}: CustomerNetworkListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useState<GetNetworksPaginatedModel>({
      // Pagination
      limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
      offset: 0,
      // Filterable fields
      customerId: [data.customer.id],
      // Sortable fields
      sortField: NETWORK_LIST_SORT_FIELD,
      sortOrder: NETWORK_LIST_SORT_ORDER,
    });

  const value = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
  ]);

  return (
    <CustomerNetworkListContext.Provider value={value}>
      {children}
    </CustomerNetworkListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface CustomerUserListContextProps {
  getPaginatedQueryParameters: GetUsersPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetUsersPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const CustomerUserListContext =
  createContext<CustomerUserListContextProps | null>(null);

export const useCustomerUserListContext = (): CustomerUserListContextProps => {
  const context = useContext(CustomerUserListContext);

  if (!context) {
    throw new Error(
      'useCustomerUserListContext must be used within CustomerUserListContext.Provider',
    );
  }

  return context;
};

interface CustomerUserListContextProviderProps {
  data: { customer: GatewayCustomerModel };
  children: React.ReactNode;
}

export const CustomerUserListContextProvider = ({
  data,
  children,
}: CustomerUserListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useState<GetUsersPaginatedModel>({
      // Pagination
      limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
      offset: 0,
      // Filterable fields
      customerId: [data.customer.id],
      // Sortable fields
      sortField: USER_LIST_SORT_FIELD,
      sortOrder: USER_LIST_SORT_ORDER,
    });

  const value = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
  ]);

  return (
    <CustomerUserListContext.Provider value={value}>
      {children}
    </CustomerUserListContext.Provider>
  );
};
